// Persistent top-of-page banner shown when the current session was
// created via an admin's "Impersonate user" action. Tells the operator
// they're not their own account + provides a one-click exit.

import { useMutation } from "@tanstack/react-query";
import { Loader2, LogOut, UserCog } from "lucide-react";

import { toast } from "@/components/toast";
import { authClient, useSession } from "@/lib/auth-client";

export function ImpersonationBanner() {
	const session = useSession();
	const impersonatedBy = (session.data?.session as { impersonatedBy?: string | null } | undefined)
		?.impersonatedBy;
	const userName = session.data?.user?.name;
	const userEmail = session.data?.user?.email;

	const stop = useMutation({
		mutationFn: async () => {
			const r = await authClient.admin.stopImpersonating();
			if (r.error) throw new Error(r.error.message ?? "stop failed");
		},
		onSuccess: () => {
			toast.info("Restored your admin session");
			// Hard navigate so the restored admin session is loaded
			// everywhere (sigma renderer, sse subscriptions, etc.).
			window.location.href = "/admin";
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't exit impersonation" }),
	});

	if (!impersonatedBy) return null;

	return (
		<div
			className="shrink-0 flex items-center justify-center gap-3 px-4 py-2 text-[12px]"
			style={{
				background:
					"linear-gradient(180deg, rgba(125,211,252,0.12), rgba(125,211,252,0.04))",
				borderBottom: "1px solid rgba(125,211,252,0.25)",
				color: "rgba(125,211,252,0.95)",
			}}
		>
			<UserCog className="w-3.5 h-3.5" />
			<span>
				You're impersonating{" "}
				<span className="mono">{userName ?? userEmail ?? "user"}</span>. Their data is what you're seeing.
			</span>
			<button
				type="button"
				onClick={() => stop.mutate()}
				disabled={stop.isPending}
				className="flex items-center gap-1.5 px-2.5 py-1 rounded-[var(--radius-pill)] border border-[rgba(125,211,252,0.3)] hover:bg-[rgba(125,211,252,0.08)] transition-colors disabled:opacity-50"
			>
				{stop.isPending ? (
					<Loader2 className="w-3 h-3 animate-spin" />
				) : (
					<LogOut className="w-3 h-3" />
				)}
				Exit impersonation
			</button>
		</div>
	);
}
